import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ChevronLeft, ChevronRight, ScrollText, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

const ACTION_LABEL = {
  create: 'Created user',
  update: 'Updated user',
  update_status: 'Changed status',
  send_password_reset: 'Sent password reset',
  delete: 'Deleted user',
};

const ACTION_COLOR = {
  create: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  update: 'text-blue-700 bg-blue-50 border-blue-200',
  update_status: 'text-amber-700 bg-amber-50 border-amber-200',
  send_password_reset: 'text-purple-700 bg-purple-50 border-purple-200',
  delete: 'text-red-700 bg-red-50 border-red-200',
};

function ChangesList({ changes }) {
  const entries = Object.entries(changes || {});
  if (entries.length === 0) return <span className="text-[#A0AEC0] italic text-xs">no field changes</span>;
  return (
    <ul className="space-y-0.5">
      {entries.map(([field, val]) => (
        <li key={field} className="text-xs">
          <span className="font-medium text-[#14213D]">{field}</span>
          <span className="text-[#A0AEC0] mx-1">:</span>
          <span className="font-mono text-stone-500">{JSON.stringify(val.old)}</span>
          <span className="text-[#A0AEC0] mx-1.5">→</span>
          <span className="font-mono text-[#14213D]">{JSON.stringify(val.new)}</span>
        </li>
      ))}
    </ul>
  );
}

export default function AuditLogList({ token }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchPage = async (p) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit-log`, {
        params: { limit: PAGE_SIZE, skip: p * PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPage(page); }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleDownloadCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/audit-log/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `atlas_history_audit_log_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit log downloaded');
    } catch {
      toast.error('Failed to download CSV');
    }
  };

  return (
    <div data-testid="audit-log-list">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="text-sm text-[#5A6577]">
          {total} action{total === 1 ? '' : 's'} logged · 90-day retention
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            disabled={total === 0 || loading}
            onClick={handleDownloadCSV}
            className="border-[#D4AF37] text-[#14213D] hover:bg-[#FFF8E7]"
            data-testid="audit-export-csv"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
          </Button>
          <Button
            variant="outline" size="sm"
            disabled={page === 0 || loading}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            data-testid="audit-prev-page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs text-[#5A6577]">Page {page + 1} of {totalPages}</span>
          <Button
            variant="outline" size="sm"
            disabled={page + 1 >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
            data-testid="audit-next-page"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#14213D] mx-auto" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-[#EBE7E0] bg-[#FDFBF7]">
          <ScrollText className="h-8 w-8 mx-auto text-[#A0AEC0] mb-2" />
          <p className="text-sm text-[#5A6577]">No admin actions logged yet.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((row) => (
            <Card key={row.id} className="p-3 border-[#EBE7E0] bg-white" data-testid={`audit-row-${row.id}`}>
              <div className="flex items-start gap-3">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${ACTION_COLOR[row.action] || 'text-stone-700 bg-stone-50 border-stone-200'} whitespace-nowrap`}
                >
                  {ACTION_LABEL[row.action] || row.action}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2 mb-1">
                    <span className="text-sm text-[#14213D]">
                      <strong>{row.admin_email}</strong>
                      <span className="text-[#A0AEC0] mx-1.5">→</span>
                      <span>{row.target_email || '—'}</span>
                    </span>
                  </div>
                  <ChangesList changes={row.changes} />
                </div>
                <span className="text-[11px] text-[#A0AEC0] whitespace-nowrap">
                  {new Date(row.timestamp).toLocaleString()}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
